/**
 * The shared vocabulary of the platform.
 *
 * Every one of these lists is also a database CHECK constraint and, in most
 * cases, the `criteria` of a Jev decision primitive. Keeping them in one file
 * is what stops the three drifting apart — a value the planner can emit but the
 * database rejects is the failure mode this file exists to prevent.
 */

/** How a belief came to be held. Determines the ceiling on its confidence. */
export const BASES = [
  'legacy_quiz',
  'inferred',
  'self_report',
  'explained',
  'document_verified',
  /* An Afra assessor reviewed the evidence and set the score (025). The only
     basis above a document, because a person checked the document. */
  'assessor_verified',
] as const
export type Basis = (typeof BASES)[number]

/**
 * The confidence ceiling per basis — the anti-gaming mechanism.
 *
 * A charity can talk its way to `explained` but never past it: only a document
 * the platform can quote raises a belief to `document_verified`. This is the
 * 1397 refusal to manufacture documentation, expressed as arithmetic so it
 * scales to charities no human at Afra will ever meet.
 *
 * `legacy_quiz` sits below `self_report` deliberately: the old 151-question
 * instrument is a self-report on a coarser scale with no room to explain, so it
 * is weaker evidence than the same claim made in conversation.
 */
export const BASIS_CEILING: Record<Basis, number> = {
  legacy_quiz: 0.45,
  inferred: 0.4,
  self_report: 0.5,
  explained: 0.7,
  document_verified: 0.95,
  assessor_verified: 0.98,
}

/** Bases a conversation may produce. Document verification is never one of them. */
export const CONVERSATIONAL_BASES = ['self_report', 'explained'] as const
export type ConversationalBasis = (typeof CONVERSATIONAL_BASES)[number]

/**
 * How the answer was qualified. A hedge caps the score: «بله، ولی خب رسمی نیست»
 * is a 1 or a 2, never a 3. The hedge is stored so the follow-up can use the
 * director's own framing rather than starting over.
 */
export const HEDGES = ['none', 'informal', 'aspirational', 'partial', 'contested'] as const
export type Hedge = (typeof HEDGES)[number]

/** Emotional read of the reply, used to pace teaching against assessing. */
export const AFFECTS = ['neutral', 'confident', 'curious', 'overwhelmed', 'defensive'] as const
export type Affect = (typeof AFFECTS)[number]

/**
 * What the agent does on a turn. The planner picks the move; the model only
 * words it.
 *
 * Phase 1 emits the first eight. The last four belong to the evidence and
 * content workstreams and are declared here so the database, the UI and the
 * planner already know their names when those phases land.
 */
export const MOVE_TYPES = [
  'orient',
  'ask',
  'probe',
  'teach',
  'commit',
  'checkpoint',
  'celebrate',
  'rest',
  // deferred — see docs/workstreams/
  'request_document',
  'challenge',
  'verify',
  'recommend',
] as const
export type MoveType = (typeof MOVE_TYPES)[number]

export const PHASE_1_MOVES: readonly MoveType[] = [
  'orient',
  'ask',
  'probe',
  'teach',
  'commit',
  'checkpoint',
  'celebrate',
  'rest',
]

/** The seven dimensions of the Afra standard, by their canon code. */
export const DIMENSION_CODES = ['GOV', 'PLAN', 'HR', 'COMM', 'FIN', 'TECH', 'IMPROVE'] as const
export type DimensionCode = (typeof DIMENSION_CODES)[number]

/**
 * Roles, in ascending order of reach.
 *
 * `member` and `org_admin` belong to a charity. `afra_admin` and `super_admin`
 * are staff: they live in the platform organization, and what they can reach is
 * every charity rather than one. `super_admin` is the only role that can appoint
 * staff or impersonate, which is why it is a role and not a flag on afra_admin.
 */
export const ROLES = ['member', 'org_admin', 'afra_admin', 'super_admin', 'assessor'] as const
export type Role = (typeof ROLES)[number]

/** The roles a charity's own account may hold. Staff are appointed, never invited. */
export const TENANT_ROLES = ['member', 'org_admin'] as const
export type TenantRole = (typeof TENANT_ROLES)[number]

/**
 * Staff roles. These are the accounts that are not scoped to one organization.
 *
 * `assessor` (025) is staff in the narrowest sense: it lives in the platform
 * organization and reviews submitted assessments across charities, and the
 * admin surface refuses it — see `isAdminRole`.
 */
export const PLATFORM_ROLES = ['afra_admin', 'super_admin', 'assessor'] as const
export type PlatformRole = (typeof PLATFORM_ROLES)[number]

export const isPlatformRole = (role: Role): role is PlatformRole =>
  role === 'afra_admin' || role === 'super_admin' || role === 'assessor'

/** The staff who run the platform. An assessor is staff but not one of these. */
export const isAdminRole = (role: Role): boolean => role === 'afra_admin' || role === 'super_admin'

/** Who may review a submitted assessment. */
export const isReviewerRole = (role: Role): boolean => isPlatformRole(role)

/**
 * What each fact is called, facing a charity.
 *
 * Here rather than in the web app because the side panel is assembled on the
 * server now, and two copies of a label list is one copy that goes stale.
 */
export const FACT_LABEL_FA: Record<string, string> = {
  respondent_role: 'نقش شما در مؤسسه',
  paid_staff: 'کارکنان تمام‌وقت',
  volunteers: 'داوطلبان',
  annual_budget: 'بودجه سالانه',
  board_size: 'اعضای هیئت مدیره',
  board_meets_per_year: 'جلسات هیئت مدیره در سال',
  beneficiaries_served: 'مددجویان',
  funding_sources: 'منابع مالی',
  funding_concentration: 'تمرکز منابع مالی',
  branches: 'شعب',
  tenure_years: 'سابقه فعالیت',
  geographic_scope: 'گستره جغرافیایی',
  has_office: 'دفتر',
  domain: 'حوزه فعالیت',
  city: 'شهر',
  mission: 'مأموریت',
  activities: 'فعالیت‌ها',
}

/**
 * Typed organizational facts, extracted as a side-effect of every turn.
 *
 * A director explaining how the board meets incidentally reveals its size, its
 * meeting frequency and whether minutes exist. That is what makes a
 * 185-statement instrument survivable as a conversation.
 */
export const FACT_CODES = [
  /* Who is talking. A volunteer with no authority and a board chair describe
     the same organization differently, and can commit it to different things. */
  'respondent_role',
  'paid_staff',
  'volunteers',
  'annual_budget',
  'board_size',
  'board_meets_per_year',
  'beneficiaries_served',
  'funding_sources',
  'funding_concentration',
  'branches',
  'tenure_years',
  'geographic_scope',
  'has_office',
  'domain',
  /* From the portal profile (024), when an admin brings it in. Context for the
     agent, never a score. */
  'city',
  'mission',
  'activities',
] as const
export type FactCode = (typeof FACT_CODES)[number]

/** Derived size band. Never affects a score — only what advice is appropriate. */
export const SIZE_BANDS = ['micro', 'small', 'medium', 'large'] as const
export type SizeBand = (typeof SIZE_BANDS)[number]

export const SIZE_BAND_FA: Record<SizeBand, string> = {
  micro: 'خُرد',
  small: 'کوچک',
  medium: 'متوسط',
  large: 'بزرگ',
}

export const DIMENSION_TITLE_FA: Record<DimensionCode, string> = {
  GOV: 'حاکمیت سازمانی',
  PLAN: 'برنامه‌ریزی، اجرا و ارزیابی',
  HR: 'سرمایه انسانی',
  COMM: 'ارتباطات',
  FIN: 'مالی',
  TECH: 'فناوری',
  IMPROVE: 'بهبود مستمر',
}

/**
 * How closely each dimension bears on a challenge found in another, 0–3, for
 * ordering the sweep across the remaining dimensions once a root cause is
 * confirmed («اول ابعاد مرتبط‌تر، بعد بقیه»).
 *
 * PROVISIONAL. The GOV and HR rows are the prototype's own
 * (docs/_prototype_mock_example.html); the rest were filled in by analogy and
 * have not been reviewed by Afra. `app_dimension_relevance` in the extract is
 * empty. Replace this when Afra supplies the real matrix — it only orders
 * questions, it never touches a score.
 */
export const DIMENSION_RELEVANCE: Record<DimensionCode, Partial<Record<DimensionCode, number>>> = {
  GOV: { PLAN: 3, IMPROVE: 3, COMM: 2, TECH: 1, FIN: 1, HR: 2 },
  HR: { IMPROVE: 3, PLAN: 2, TECH: 2, COMM: 1, FIN: 1, GOV: 2 },
  PLAN: { GOV: 3, IMPROVE: 3, FIN: 2, HR: 2, TECH: 1, COMM: 1 },
  FIN: { GOV: 3, PLAN: 2, TECH: 2, COMM: 2, HR: 1, IMPROVE: 1 },
  COMM: { GOV: 2, PLAN: 2, TECH: 2, HR: 1, FIN: 1, IMPROVE: 1 },
  TECH: { PLAN: 2, FIN: 2, COMM: 2, HR: 1, IMPROVE: 2, GOV: 1 },
  IMPROVE: { PLAN: 3, GOV: 2, HR: 2, TECH: 1, COMM: 1, FIN: 1 },
}

/** Maturity is the standard's own 0–3 scale. Not a re-invention. */
export const MIN_SCORE = 0
export const MAX_SCORE = 3

/**
 * The k-anonymity floor for anything derived across organizations.
 *
 * A cohort cell below this is suppressed *before* a model ever sees it, so
 * there is no prompt to argue around.
 */
export const K_ANONYMITY_FLOOR = 5
